import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Logger } from '@core/logger';
import { ICON } from '@core/util/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { ClientsRepoService } from '@repos/clients/clients.service';
import { NavigationService } from '@services/nav/nav.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import {
	CloserLeapClientMap,
	ClientMatch,
	CloserToLeapClientSyncTask,
	PersonMatch,
} from '@state/closer-tasks/closerTasks.model';
import { CloserTasksState } from '@state/closer-tasks/closerTasks.state';
import { Timestamp } from 'firebase/firestore';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
	auditTime,
	catchError,
	filter,
	finalize,
	map,
	take,
	takeUntil,
} from 'rxjs/operators';
import { LeapApiService } from 'src/app/shared/api-repos/leap/leap-api.service';
import { ButtonGroupOption } from 'src/app/shared/atomic/atoms/button-group/button-group.component';
import { BUTTON_PRESETS } from 'src/app/ui/button/button.component';

export enum ClientsMergeReviewPage {
	CLIENTS_MERGE_REPORT = 'clients-merge-report',
	FIX_CLIENTS_LIST = 'fix-clients-list',
}

enum PresetRadioValue {
	CREATE_AS_NEW_CLIENT = 'createAsNewClient',
}

interface ResolvedUnmergedClientsTracker {
	mergedClients: number;
	totalResolvedClients: number;
}

interface MergedClientData {
	name: string;
	email: string;
	birthDate: string;
}

interface LeapMatchIds {
	leapCardId: string;
	personId: string | null;
}

interface UnmergedClientLeapMatch {
	leapCardId: string;
	personId: string | null;
	name: string;
	birthDate: string;
	email: string;
	radioValue: LeapMatchIds;
}

interface UnmergedClientData {
	closerClientId: string;
	name: string;
	email: string;
	birthDate: string;
	lastActiveFileDate: string;
	leapMatches: UnmergedClientLeapMatch[];
}

const DefaultMergeReviewSelectionOptions: ButtonGroupOption<ClientsMergeReviewPage>[] =
	[
		{
			label: 'Merged Clients (0)',
			value: ClientsMergeReviewPage.CLIENTS_MERGE_REPORT,
		},
		{
			label: 'Merge and Fix (0)',
			value: ClientsMergeReviewPage.FIX_CLIENTS_LIST,
		},
	];

@UntilDestroy()
@Component({
	selector: 'app-client-merge-review',
	templateUrl: './client-merge-review.component.html',
	styleUrls: ['./client-merge-review.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ClientMergeReviewComponent implements OnInit {
	@Select(CloserTasksState.closerToLeapClientSyncTask)
	closerToLeapClientSyncTask$: Observable<CloserToLeapClientSyncTask>;

	mergedClientsTableColumns = ['name', 'email'];

	currSelectedPage$ = new BehaviorSubject<ClientsMergeReviewPage>(
		ClientsMergeReviewPage.CLIENTS_MERGE_REPORT,
	);

	mergeReviewSelectionOptions$ = new BehaviorSubject<
		ButtonGroupOption<ClientsMergeReviewPage>[]
	>(structuredClone(DefaultMergeReviewSelectionOptions));

	mergedClientsDataSource$ = new BehaviorSubject<MergedClientData[]>([]);
	unmergedClientsData$ = new BehaviorSubject<UnmergedClientData[]>([]);

	resolvedUnmergedClientsTracker$ =
		new BehaviorSubject<ResolvedUnmergedClientsTracker>({
			mergedClients: 0,
			totalResolvedClients: 0,
		});

	mergeableClientsCount$: Observable<number>;

	clientExportInProgress$ = new BehaviorSubject<boolean>(false);

	// Mapping Closer client ids to Leap card ids for unmerged clients (keys are Closer client ids, value is either Leap ids or a string representing the option to create it as a new client)
	unmergedClientsRadioValuesModel$ = new BehaviorSubject<
		Record<string, PresetRadioValue.CREATE_AS_NEW_CLIENT | LeapMatchIds>
	>({});

	BUTTON_PRESETS = BUTTON_PRESETS;
	ClientsMergeReviewPage = ClientsMergeReviewPage;
	PresetRadioValue = PresetRadioValue;

	constructor(
		private route: ActivatedRoute,
		private navService: NavigationService,
		private clientsRepo: ClientsRepoService,
		private leapApiService: LeapApiService,
		private snackbarService: SnackbarService,
	) {}

	ngOnInit(): void {
		this.route.paramMap.subscribe((params) => {
			const subsection = params.get('subsection');

			switch (subsection) {
				case ClientsMergeReviewPage.CLIENTS_MERGE_REPORT:
					this.currSelectedPage$.next(
						ClientsMergeReviewPage.CLIENTS_MERGE_REPORT,
					);
					break;
				case ClientsMergeReviewPage.FIX_CLIENTS_LIST:
					this.currSelectedPage$.next(ClientsMergeReviewPage.FIX_CLIENTS_LIST);
					break;
				default:
					this.navService.goToProfile_Integrations_Leap_ClientsMergeReport();
					break;
			}
		});

		this.clientsRepo
			.clientsExportedToLeap()
			.pipe(untilDestroyed(this), auditTime(300))
			.subscribe((exportedClients) => {
				const mergedClientsData: MergedClientData[] = exportedClients.map(
					(client) => ({
						name:
							client.name ??
							this.constructFullName({
								firstName: client['firstName'],
								middleNames: client['middleNames'],
								lastName: client['lastName'],
							}),
						email: client.email || '',
						birthDate: this.parseClientBirthDateString(client['dateOfBirth']),
					}),
				);

				const updatedSelectionOptions = this.getUpdatedMergeSelectionOptions({
					mergedClientsCount: mergedClientsData.length,
				});

				this.mergedClientsDataSource$.next(mergedClientsData);

				this.mergeReviewSelectionOptions$.next(updatedSelectionOptions);
			});

		this.closerToLeapClientSyncTask$
			.pipe(
				untilDestroyed(this),
				auditTime(500),
				filter((task) => !!task?.results?.completionDateTimeStamp),
			)
			.subscribe((task) => {
				const { personMatches, clientMatches } = task.results;

				// get combined and sorted unmerged clients data
				const unmergedClientsData = this.getUnmergedClientsDataFromMatches(
					personMatches,
					clientMatches,
				);

				// build new radio values model for unmerged clients
				const unmergedClientsRadioValuesModel =
					this.buildUnmergedClientsRadioValuesModel(unmergedClientsData);

				// get updated client counts for tab btn labels
				const updatedMergeSelectionOptions =
					this.getUpdatedMergeSelectionOptions({
						unmergedClientsCount: unmergedClientsData.length,
					});

				this.unmergedClientsRadioValuesModel$.next(
					unmergedClientsRadioValuesModel,
				);

				this.unmergedClientsData$.next(unmergedClientsData);

				this.mergeReviewSelectionOptions$.next(updatedMergeSelectionOptions);
			});

		this.mergeableClientsCount$ = this.unmergedClientsRadioValuesModel$.pipe(
			untilDestroyed(this),
			map((radioValues) => {
				const mergeableClientsCount = Object.values(radioValues).filter(
					(val) => !!val,
				).length;

				return mergeableClientsCount;
			}),
		);
	}

	onPageSelectionChange(selectedPage: ClientsMergeReviewPage): void {
		if (selectedPage === ClientsMergeReviewPage.CLIENTS_MERGE_REPORT) {
			this.currSelectedPage$.next(ClientsMergeReviewPage.CLIENTS_MERGE_REPORT);
			this.navService.goToProfile_Integrations_Leap_ClientsMergeReport();
			return;
		}

		if (selectedPage === ClientsMergeReviewPage.FIX_CLIENTS_LIST) {
			this.currSelectedPage$.next(ClientsMergeReviewPage.FIX_CLIENTS_LIST);
			this.navService.goToProfile_Integrations_Leap_FixClientsList();
			return;
		}
	}

	backToLeapIntegrationPage() {
		this.navService.goToProfile_Integrations_Leap();
	}

	removeUnmergedClient(targetCloserClientId: string) {
		const updatedRadioValuesModel = structuredClone(
			this.unmergedClientsRadioValuesModel$.value,
		);

		delete updatedRadioValuesModel[targetCloserClientId];

		this.unmergedClientsRadioValuesModel$.next(updatedRadioValuesModel);

		const currUnmergedClientsData = structuredClone(
			this.unmergedClientsData$.value,
		);

		const updatedUnmergedClientsData = currUnmergedClientsData.filter(
			(clientData) => clientData.closerClientId !== targetCloserClientId,
		);

		const updatedSelectionOptions = this.getUpdatedMergeSelectionOptions({
			unmergedClientsCount: updatedUnmergedClientsData.length,
		});

		this.unmergedClientsData$.next(updatedUnmergedClientsData);

		this.mergeReviewSelectionOptions$.next(updatedSelectionOptions);
	}

	/** Finds the closerClientId with an identical conflict resolution selection as the current one and returns it, will return null if no match found  */
	findDuplicateSelection(
		radioControlValue: PresetRadioValue.CREATE_AS_NEW_CLIENT | LeapMatchIds,
	) {
		if (radioControlValue === PresetRadioValue.CREATE_AS_NEW_CLIENT) {
			return null;
		}

		const activeClientRadioValues = Object.entries(
			this.unmergedClientsRadioValuesModel$.value,
		);

		for (const [
			closerClientId,
			activeResolutionChoice,
		] of activeClientRadioValues) {
			if (
				!activeResolutionChoice ||
				activeResolutionChoice === PresetRadioValue.CREATE_AS_NEW_CLIENT
			) {
				continue;
			}

			const { leapCardId, personId } = activeResolutionChoice;

			if (
				leapCardId === radioControlValue.leapCardId &&
				personId === radioControlValue.personId
			) {
				return closerClientId;
			}
		}

		return null;
	}

	unmergedClientRadioHandler(
		radioControlValue: PresetRadioValue.CREATE_AS_NEW_CLIENT | LeapMatchIds,
		closerClientId: string,
	) {
		const closerClientIdToBeReset =
			this.findDuplicateSelection(radioControlValue);

		const updatedRadioValues = {
			...this.unmergedClientsRadioValuesModel$.value,
			[closerClientId]: radioControlValue,
		};

		if (closerClientIdToBeReset) {
			this.snackbarService.openSnackBar({
				text: 'This client has been selected more than once. The previous selection has been removed.',
				type: 'info',
				icon: ICON.INFO,
			});

			updatedRadioValues[closerClientIdToBeReset] = null;
		}

		this.unmergedClientsRadioValuesModel$.next(updatedRadioValues);
	}

	getResolvableUnmergedClients() {
		const mappedCloserLeapClients: CloserLeapClientMap[] = Object.entries(
			this.unmergedClientsRadioValuesModel$.value,
		).reduce((acc, [closerClientId, resolutionDetails]) => {
			if (!resolutionDetails) return acc;

			if (resolutionDetails === PresetRadioValue.CREATE_AS_NEW_CLIENT) {
				acc.push({
					closerClientId,
				});
			} else {
				acc.push({
					closerClientId,
					leap: {
						cardId: resolutionDetails.leapCardId,
						personId: resolutionDetails.personId,
					},
				});
			}

			return acc;
		}, [] as CloserLeapClientMap[]);

		const clientMergeStateArr: [string, boolean][] =
			mappedCloserLeapClients.map((client) => [client.closerClientId, false]);
		const clientMergeStateTracker = new Map<string, boolean>(
			clientMergeStateArr,
		);

		return { mappedCloserLeapClients, clientMergeStateTracker };
	}

	async exportUnmergedClientsToLeap() {
		Logger.info('Exporting resolved unmerged clients into Leap');

		const { mappedCloserLeapClients, clientMergeStateTracker } =
			this.getResolvableUnmergedClients();
		const totalClientsToResolve = clientMergeStateTracker.size;

		this.resolvedUnmergedClientsTracker$.next({
			mergedClients: 0,
			totalResolvedClients: totalClientsToResolve,
		});

		this.clientExportInProgress$.next(true);

		this.closerToLeapClientSyncTask$
			.pipe(
				untilDestroyed(this),
				takeUntil(
					this.leapApiService.resolveConflicts(mappedCloserLeapClients).pipe(
						take(1),
						catchError((err) => {
							Logger.error(
								`Failed to export resolved unmerged clients into Leap: ${JSON.stringify(err)}`,
							);

							this.snackbarService.error(
								'Unable to merge and fix the selected clients. Please try again.',
							);

							return of(undefined);
						}),
					),
				),
				auditTime(2500),
				finalize(() => {
					this.resolvedUnmergedClientsTracker$.next({
						mergedClients: 0,
						totalResolvedClients: 0,
					});

					this.clientExportInProgress$.next(false);
				}),
			)
			.subscribe((task) => {
				task?.results.personMatches.forEach((personMatch) => {
					const closerId = personMatch.closerClientPerson.id;

					if (personMatch.merged && clientMergeStateTracker.has(closerId)) {
						clientMergeStateTracker.set(closerId, true);
					}
				});

				task?.results.clientMatches.forEach((clientMatch) => {
					const closerId = clientMatch.closerClient.id;

					if (clientMatch.merged && clientMergeStateTracker.has(closerId)) {
						clientMergeStateTracker.set(closerId, true);
					}
				});

				let currMergedCountsCount = 0;

				clientMergeStateTracker.forEach((value) => {
					if (value === false) {
						currMergedCountsCount++;
					}
				});

				this.resolvedUnmergedClientsTracker$.next({
					...this.resolvedUnmergedClientsTracker$.value,
					mergedClients: currMergedCountsCount,
				});
			});
	}

	getUnmergedClientsDataFromMatches(
		allPersonMatches: PersonMatch[],
		allClientMatches: ClientMatch[],
	): UnmergedClientData[] {
		const unmergedClientData: UnmergedClientData[] = [];

		const filteredPersonMatches = allPersonMatches.filter((p) => !p.merged);
		const filteredClientMatches = allClientMatches.filter((c) => !c.merged);

		const sortedPersonMatches = filteredPersonMatches.sort((a, b) => {
			const fullNameA = this.constructFullName(a.closerClientPerson);
			const fullNameB = this.constructFullName(b.closerClientPerson);

			return fullNameA.localeCompare(fullNameB);
		});

		const sortedClientMatches = filteredClientMatches.sort((a, b) => {
			const nameA = a.closerClient.name ?? '';
			const nameB = b.closerClient.name ?? '';

			return nameA.localeCompare(nameB);
		});

		sortedPersonMatches.forEach((personMatch) => {
			const fullName = this.constructFullName(personMatch.closerClientPerson);

			unmergedClientData.push({
				closerClientId: personMatch.closerClientPerson.id,
				name: fullName,
				email: personMatch.closerClientPerson.emailAddress ?? '',
				birthDate: this.parseClientBirthDateString(
					personMatch.closerClientPerson?.birthDate,
				),
				lastActiveFileDate: this.parseFirestoreTimestamp(
					personMatch.closerClientPerson?.lastActiveFileDate,
				),
				leapMatches:
					personMatch?.leapMatches?.map((leapMatch) => ({
						leapCardId: null,
						personId: leapMatch.personId,
						name: this.constructFullName(leapMatch),
						email: leapMatch.emailAddress ?? '',
						birthDate: this.parseClientBirthDateString(leapMatch.birthDate),
						radioValue: {
							leapCardId: personMatch.leapCardId ?? null,
							personId: leapMatch.personId,
						},
					})) || [],
			});
		});

		sortedClientMatches.forEach((clientMatch) => {
			const clientMatchData: UnmergedClientData = {
				closerClientId: clientMatch.closerClient.id,
				name: clientMatch.closerClient.name,
				email: clientMatch.closerClient.emailAddress ?? '',
				birthDate: this.parseClientBirthDateString(
					clientMatch.closerClient.birthDate,
				),
				lastActiveFileDate: this.parseFirestoreTimestamp(
					clientMatch.closerClient?.lastActiveFileDate,
				),
				leapMatches: [],
			};

			// Expect: clientMatch.leapPersonMatches to be an array that will contain items only when clientMatch.leapMatches has exactly one item,
			// present clientMatch.leapMatches items to the user as potential matches to resolve conflicts if this was the case,
			// otherwise, present clientMatch.leapMatches items as potential matches to resolve conflicts
			if (
				clientMatch?.leapMatches?.length === 1 &&
				Array.isArray(clientMatch?.leapPersonMatches)
			) {
				const leapCardId = clientMatch.leapMatches.at(0).cardId;

				clientMatch.leapPersonMatches.forEach((leapPersonMatch) => {
					const fullName = this.constructFullName(leapPersonMatch);

					clientMatchData.leapMatches.push({
						leapCardId,
						personId: leapPersonMatch.personId,
						name: fullName,
						email: leapPersonMatch.emailAddress ?? '',
						birthDate: leapPersonMatch.birthDate ?? '',
						radioValue: {
							leapCardId,
							personId: leapPersonMatch.personId,
						},
					});
				});
			} else {
				clientMatch?.leapMatches?.forEach((leapMatch) => {
					clientMatchData.leapMatches.push({
						leapCardId: leapMatch.cardId,
						personId: null,
						name: leapMatch.fullName,
						email: leapMatch.emailAddress ?? '',
						birthDate: '',
						radioValue: {
							leapCardId: leapMatch.cardId,
							personId: null,
						},
					});
				});
			}

			unmergedClientData.push(clientMatchData);
		});

		return unmergedClientData;
	}

	getUpdatedMergeSelectionOptions(updatedCounts: {
		mergedClientsCount?: number;
		unmergedClientsCount?: number;
	}) {
		const {
			mergedClientsCount: mergedClients,
			unmergedClientsCount: unmergedClients,
		} = updatedCounts;
		const updatedMergedClientOptions = structuredClone(
			this.mergeReviewSelectionOptions$.value,
		);

		if (typeof updatedCounts.mergedClientsCount === 'number') {
			updatedMergedClientOptions[0].label = `Merged Clients (${mergedClients})`;
		}

		if (typeof updatedCounts.unmergedClientsCount === 'number') {
			updatedMergedClientOptions[1].label = `Merge and Fix (${unmergedClients})`;
		}

		return updatedMergedClientOptions;
	}

	buildUnmergedClientsRadioValuesModel(
		unmergedClientsData: UnmergedClientData[],
	) {
		const radioValuesModel: Record<
			string,
			PresetRadioValue.CREATE_AS_NEW_CLIENT | LeapMatchIds
		> = {};

		unmergedClientsData.forEach((clientData) => {
			radioValuesModel[clientData.closerClientId] = null;
		});

		return radioValuesModel;
	}

	// Returns date string in MM/DD/YYYY format
	parseFirestoreTimestamp(firestoreTimestamp: Timestamp) {
		if (!(firestoreTimestamp instanceof Timestamp)) return '';

		const date = firestoreTimestamp.toDate();
		const options: Intl.DateTimeFormatOptions = {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		};
		return date.toLocaleDateString('en-US', options);
	}

	// Expects birthDate string in YYYY-MM-DD format, returns date string in MM/DD/YYYY format
	parseClientBirthDateString = (birthDate: string) => {
		if (!birthDate) return '';

		const [year, month, day] = birthDate.split('-');

		if (!year || !month || !day) return '';

		return `${month}/${day}/${year}`;
	};

	constructFullName(entity: {
		firstName: string;
		middleNames: string;
		lastName: string;
	}) {
		return `${entity.firstName ?? ''}${entity.middleNames ? ' ' + entity.middleNames + ' ' : ' '}${entity.lastName ?? ''}`;
	}
}
